/**
 * Institution API services
 */
import { api } from './api';
import type { ApiResponse } from '@/types';

// Helper to convert snake_case to camelCase
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Transform object keys from snake_case to camelCase
function transformKeys<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item)) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(value);
    }
    return result as T;
  }
  return obj as T;
}

// Wrap API call with snake_case to camelCase transformation
async function transformedGet<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    return {
      ...response,
      data: transformKeys<T>(response.data),
    };
  }
  return response as ApiResponse<T>;
}

// Types
export interface DashboardStats {
  totalDocuments: number;
  pendingTasks: number;
  completedThisWeek: number;
  overdueTasks: number;
}

export interface RecentDocument {
  id: string;
  title: string;
  referenceNumber: string | null;
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
  documentType: {
    id: string;
    name: string;
    code: string;
  };
  status: {
    id: string;
    name: string;
    color: string;
  };
}

export interface PendingTask {
  id: string;
  status: string;
  dueAt: string | null;
  createdAt: string;
  step: {
    name: string;
    actionRequired: string;
  };
  document: {
    id: string;
    title: string;
  };
  instanceId: string;
}

export interface ActivityItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface DocumentType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  allowedExtensions: string;
  maxFileSizeMb: number;
  requiresApproval: boolean;
  isActive: boolean;
}

export interface SecurityClassification {
  id: string;
  level: number;
  code: string;
  name: string;
  description: string | null;
  color: string;
}

export interface DocumentStatus {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string;
  isFinal: boolean;
  allowEdit: boolean;
}

// Dashboard API
export const institutionDashboard = {
  getStats: () => transformedGet<DashboardStats>('/v1/dashboard/stats'),
  getRecentDocuments: () => transformedGet<RecentDocument[]>('/v1/dashboard/recent-documents'),
  getPendingTasks: () => transformedGet<PendingTask[]>('/v1/dashboard/pending-tasks'),
  getActivity: () => transformedGet<ActivityItem[]>('/v1/dashboard/activity'),
};

// Reference data API (for creating documents)
export const institutionConfig = {
  getDocumentTypes: () => transformedGet<DocumentType[]>('/v1/config/document-types'),
  getSecurityClassifications: () => transformedGet<SecurityClassification[]>('/v1/config/security-classifications'),
  getDocumentStatuses: () => transformedGet<DocumentStatus[]>('/v1/config/document-statuses'),
};

// User types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string | null;
  jobTitle: string | null;
  phone: string | null;
  isActive: boolean;
  securityClearance: number;
  createdAt: string;
  updatedAt: string;
  department: {
    id: string;
    name: string;
    code: string;
  } | null;
  roles: {
    id: string;
    name: string;
    code: string;
  }[];
}

export interface CreateUserInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  employeeId?: string;
  jobTitle?: string;
  phone?: string;
  departmentId?: string;
  securityClearance?: number;
  roleIds?: string[];
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  jobTitle?: string;
  phone?: string;
  departmentId?: string;
  securityClearance?: number;
}

export interface PaginatedMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface PaginatedApiResponse<T> extends ApiResponse<T> {
  meta?: PaginatedMeta;
}

// Helper for paginated GET with transformation
async function transformedGetWithMeta<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<PaginatedApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    const rawMeta = response.meta as PaginatedMeta | undefined;
    return {
      ...response,
      data: transformKeys<T>(response.data),
      meta: rawMeta,
    };
  }
  return response as PaginatedApiResponse<T>;
}

// Bulk Import types
export interface BulkImportUserRow {
  firstName: string;
  lastName: string;
  email: string;
  staffId?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  departmentCode?: string | null;
  securityClearance?: number;
  roleCode?: string | null;
}

export interface BulkImportInput {
  users: BulkImportUserRow[];
  generatePasswords?: boolean;
  sendWelcomeEmail?: boolean;
}

export interface BulkImportRowResult {
  row: number;
  success: boolean;
  email: string;
  userId?: string;
  pfileId?: string;
  error?: string;
}

export interface BulkImportResult {
  success: boolean;
  totalProcessed: number;
  successCount: number;
  failureCount: number;
  results: BulkImportRowResult[];
}

// Users API
export const institutionUsers = {
  list: (params?: { page?: number; perPage?: number; departmentId?: string; isActive?: boolean; search?: string }) =>
    transformedGetWithMeta<User[]>('/v1/users', params as Record<string, string | number | boolean | undefined>),

  getById: (id: string) => transformedGet<User>(`/v1/users/${id}`),

  create: async (input: CreateUserInput): Promise<ApiResponse<User>> => {
    const response = await api.post<unknown>('/v1/users', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<User>(response.data) };
    }
    return response as ApiResponse<User>;
  },

  update: async (id: string, input: UpdateUserInput): Promise<ApiResponse<User>> => {
    const response = await api.put<unknown>(`/v1/users/${id}`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<User>(response.data) };
    }
    return response as ApiResponse<User>;
  },

  deactivate: (id: string) => api.delete(`/v1/users/${id}`),

  activate: (id: string) => api.post(`/v1/users/${id}/activate`, {}),

  updateRoles: async (id: string, roleIds: string[]): Promise<ApiResponse<User>> => {
    const response = await api.put<unknown>(`/v1/users/${id}/roles`, { roleIds });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<User>(response.data) };
    }
    return response as ApiResponse<User>;
  },

  resetPassword: (id: string, newPassword: string) =>
    api.post(`/v1/users/${id}/reset-password`, { newPassword }),

  bulkImport: async (input: BulkImportInput): Promise<ApiResponse<BulkImportResult>> => {
    const response = await api.post<unknown>('/v1/users/bulk-import', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<BulkImportResult>(response.data) };
    }
    return response as ApiResponse<BulkImportResult>;
  },
};

// Department types
export interface Department {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId: string | null;
  headUserId: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CreateDepartmentInput {
  code: string;
  name: string;
  description?: string;
  parentId?: string;
  headUserId?: string;
}

export interface UpdateDepartmentInput {
  code?: string;
  name?: string;
  description?: string;
  parentId?: string;
  headUserId?: string;
  isActive?: boolean;
}

// Departments API
export const institutionDepartments = {
  list: () => transformedGet<Department[]>('/v1/departments'),
  getById: (id: string) => transformedGet<Department>(`/v1/departments/${id}`),
  getTree: () => transformedGet<Department[]>('/v1/departments/tree'),
  getUsers: (id: string) => transformedGet<User[]>(`/v1/departments/${id}/users`),

  create: async (input: CreateDepartmentInput): Promise<ApiResponse<Department>> => {
    const response = await api.post<unknown>('/v1/departments', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Department>(response.data) };
    }
    return response as ApiResponse<Department>;
  },

  update: async (id: string, input: UpdateDepartmentInput): Promise<ApiResponse<Department>> => {
    const response = await api.put<unknown>(`/v1/departments/${id}`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Department>(response.data) };
    }
    return response as ApiResponse<Department>;
  },

  delete: (id: string) => api.delete(`/v1/departments/${id}`),
};

// Role types
export interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystemRole: boolean;
  createdAt: string;
}

export interface CreateRoleInput {
  code: string;
  name: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleInput {
  code?: string;
  name?: string;
  description?: string;
  permissions?: string[];
}

export interface Permission {
  code: string;
  name: string;
  category: string;
}

// Roles API
export const institutionRoles = {
  list: () => transformedGet<Role[]>('/v1/roles'),
  getById: (id: string) => transformedGet<Role>(`/v1/roles/${id}`),
  getPermissions: () => transformedGet<Permission[]>('/v1/roles/permissions'),
  getUsers: (id: string) => transformedGet<User[]>(`/v1/roles/${id}/users`),

  create: async (input: CreateRoleInput): Promise<ApiResponse<Role>> => {
    const response = await api.post<unknown>('/v1/roles', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Role>(response.data) };
    }
    return response as ApiResponse<Role>;
  },

  update: async (id: string, input: UpdateRoleInput): Promise<ApiResponse<Role>> => {
    const response = await api.put<unknown>(`/v1/roles/${id}`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Role>(response.data) };
    }
    return response as ApiResponse<Role>;
  },

  delete: (id: string) => api.delete(`/v1/roles/${id}`),
};

// Workflow Task types
export interface WorkflowTask {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'delegated';
  dueAt: string | null;
  createdAt: string;
  completedAt: string | null;
  comments: string | null;
  step: {
    id: string;
    name: string;
    actionRequired: string;
    allowDelegate: boolean;
    allowReject: boolean;
  };
  document: {
    id: string;
    title: string;
    referenceNumber: string | null;
  };
  instance: {
    id: string;
    status: string;
  };
  assignedBy: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

// Workflow Template types
export interface WorkflowStep {
  id: string;
  stepOrder: number;
  name: string;
  description: string | null;
  assigneeType: 'user' | 'role' | 'department';
  assigneeId: string;
  actionRequired: 'approve' | 'review' | 'sign' | 'acknowledge';
  dueDays: number | null;
  allowDelegate: boolean;
  allowReject: boolean;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string | null;
  documentTypeId: string | null;
  isActive: boolean;
  createdAt: string;
  steps?: WorkflowStep[];
}

export interface CreateWorkflowStepInput {
  stepOrder: number;
  name: string;
  description?: string;
  assigneeType: 'user' | 'role' | 'department';
  assigneeId: string;
  actionRequired: 'approve' | 'review' | 'sign' | 'acknowledge';
  dueDays?: number;
  allowDelegate?: boolean;
  allowReject?: boolean;
}

export interface CreateWorkflowTemplateInput {
  name: string;
  description?: string;
  documentTypeId?: string;
  steps: CreateWorkflowStepInput[];
}

export interface WorkflowInstance {
  id: string;
  templateId: string;
  documentId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  currentStepId: string | null;
  startedBy: string;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  template?: { id: string; name: string };
  document?: { id: string; title: string };
  tasks?: WorkflowTask[];
}

// Workflows API
export const institutionWorkflows = {
  // Templates
  getTemplates: () => transformedGet<WorkflowTemplate[]>('/v1/workflows/templates'),

  getTemplateById: (id: string) => transformedGet<WorkflowTemplate>(`/v1/workflows/templates/${id}`),

  createTemplate: async (input: CreateWorkflowTemplateInput): Promise<ApiResponse<WorkflowTemplate>> => {
    const response = await api.post<unknown>('/v1/workflows/templates', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<WorkflowTemplate>(response.data) };
    }
    return response as ApiResponse<WorkflowTemplate>;
  },

  deleteTemplate: (id: string) => api.delete(`/v1/workflows/templates/${id}`),

  // Workflow Instances
  startWorkflow: async (templateId: string, documentId: string): Promise<ApiResponse<WorkflowInstance>> => {
    const response = await api.post<unknown>('/v1/workflows/start', { templateId, documentId });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<WorkflowInstance>(response.data) };
    }
    return response as ApiResponse<WorkflowInstance>;
  },

  getInstanceById: (id: string) => transformedGet<WorkflowInstance>(`/v1/workflows/instances/${id}`),

  cancelInstance: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post<unknown>(`/v1/workflows/instances/${id}/cancel`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<{ message: string }>(response.data) };
    }
    return response as ApiResponse<{ message: string }>;
  },

  getWorkflowsForDocument: (documentId: string) =>
    transformedGet<WorkflowInstance[]>(`/v1/workflows/document/${documentId}`),

  // Tasks
  getTasks: (params?: { page?: number; perPage?: number; status?: string }) =>
    transformedGetWithMeta<WorkflowTask[]>('/v1/workflows/tasks', params as Record<string, string | number | boolean | undefined>),

  getTaskById: (id: string) => transformedGet<WorkflowTask>(`/v1/workflows/tasks/${id}`),

  getPendingCount: () => transformedGet<{ count: number }>('/v1/workflows/tasks/pending/count'),

  performAction: async (taskId: string, action: { action: 'approve' | 'reject' | 'delegate'; comments?: string; delegateTo?: string }): Promise<ApiResponse<WorkflowTask>> => {
    const response = await api.post<unknown>(`/v1/workflows/tasks/${taskId}/action`, action);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<WorkflowTask>(response.data) };
    }
    return response as ApiResponse<WorkflowTask>;
  },
};

// Recycle Bin types
export interface DeletedDocument {
  id: string;
  title: string;
  referenceNumber: string | null;
  deletedAt: string;
  deletedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  documentType: {
    id: string;
    name: string;
    code: string;
  };
  securityClassification: {
    id: string;
    name: string;
    level: number;
    color: string;
  };
}

// Recycle Bin API
export const institutionRecycleBin = {
  list: (params?: { page?: number; perPage?: number }) =>
    transformedGetWithMeta<DeletedDocument[]>('/v1/recycle-bin', params as Record<string, string | number | boolean | undefined>),

  getCount: () => transformedGet<{ count: number }>('/v1/recycle-bin/count'),

  restore: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post<unknown>(`/v1/recycle-bin/${id}/restore`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<{ message: string }>(response.data) };
    }
    return response as ApiResponse<{ message: string }>;
  },

  permanentDelete: (id: string) => api.delete(`/v1/recycle-bin/${id}`),

  emptyBin: () => api.delete('/v1/recycle-bin'),
};

// Document types for file operations
export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storageKey: string;
  checksum: string;
  changeNotes: string | null;
  createdBy: string;
  createdAt: string;
}

export interface DocumentDetail {
  id: string;
  title: string;
  description: string | null;
  referenceNumber: string | null;
  currentVersion: number;
  hasFile: boolean;
  createdAt: string;
  updatedAt: string;
  documentType: {
    id: string;
    name: string;
    code: string;
  };
  securityClassification: {
    id: string;
    name: string;
    color: string;
    level: number;
  };
  status: {
    id: string;
    name: string;
    color: string;
    code: string;
  };
  subjectUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string | null;
  } | null;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  versions: DocumentVersion[];
}

export interface AccessLog {
  id: string;
  action: string;
  ipAddress: string | null;
  userAgent: string | null;
  accessedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

// Search types
export interface SearchResult {
  id: string;
  title: string;
  description: string | null;
  referenceNumber: string | null;
  typeName: string;
  classificationName: string;
  statusName: string;
  createdAt: string;
  updatedAt: string;
}

export interface SearchFilters {
  documentTypes: { id: string; name: string; code: string }[];
  statuses: { id: string; name: string; code: string }[];
  classifications: { id: string; name: string; level: number }[];
}

export interface SearchParams {
  query: string;
  page?: number;
  perPage?: number;
  documentTypeId?: string;
  statusId?: string;
  securityClassificationId?: string;
  dateFrom?: string;
  dateTo?: string;
  subjectUserId?: string;
  [key: string]: string | number | boolean | undefined;
}

// Search API
export const institutionSearch = {
  search: (params: SearchParams) =>
    transformedGetWithMeta<SearchResult[]>('/v1/search', params as Record<string, string | number | boolean | undefined>),

  getSuggestions: (query: string) =>
    transformedGet<{ id: string; title: string }[]>(`/v1/search/suggestions?q=${encodeURIComponent(query)}`),

  getFilters: () => transformedGet<SearchFilters>('/v1/search/filters'),
};

// Reports types
export interface RecordStats {
  totalRecords: number;
  recordsByType: { typeId: string; typeName: string; count: number }[];
  recordsByStatus: { statusId: string; statusName: string; count: number }[];
  recordsByClassification: { classificationId: string; classificationName: string; count: number }[];
  recordsCreatedToday: number;
  recordsCreatedThisWeek: number;
  recordsCreatedThisMonth: number;
}

export interface StaffRecordStats {
  totalStaffWithRecords: number;
  recordsPerStaff: { userId: string; name: string; employeeId: string | null; recordCount: number }[];
  staffByDepartment: { departmentId: string; departmentName: string; staffCount: number; recordCount: number }[];
}

export interface WorkflowStats {
  totalWorkflows: number;
  activeWorkflows: number;
  completedWorkflows: number;
  rejectedWorkflows: number;
  pendingTasks: number;
  overdueTasks: number;
  avgCompletionDays: number;
  workflowsByTemplate: { templateId: string; templateName: string; count: number }[];
}

export interface ExportRecord {
  id: string;
  title: string;
  referenceNumber: string | null;
  recordType: string;
  classification: string;
  status: string;
  staffName: string | null;
  staffEmployeeId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Reports API
export const institutionReports = {
  getRecordStats: () => transformedGet<RecordStats>('/v1/reports/records'),

  getStaffStats: () => transformedGet<StaffRecordStats>('/v1/reports/staff'),

  getWorkflowStats: () => transformedGet<WorkflowStats>('/v1/reports/workflows'),

  exportRecords: (params?: {
    documentTypeId?: string;
    statusId?: string;
    classificationId?: string;
    subjectUserId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => transformedGet<ExportRecord[]>('/v1/reports/export', params as Record<string, string | number | boolean | undefined>),
};

// Documents API
export const institutionDocuments = {
  getById: async (id: string): Promise<ApiResponse<DocumentDetail>> => {
    const response = await api.get<unknown>(`/v1/documents/${id}`);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentDetail>(response.data) };
    }
    return response as ApiResponse<DocumentDetail>;
  },

  update: async (id: string, input: {
    title?: string;
    description?: string;
    documentTypeId?: string;
    securityClassificationId?: string;
    subjectUserId?: string | null;
    referenceNumber?: string;
  }): Promise<ApiResponse<DocumentDetail>> => {
    const response = await api.put<unknown>(`/v1/documents/${id}`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentDetail>(response.data) };
    }
    return response as ApiResponse<DocumentDetail>;
  },

  delete: (id: string) => api.delete(`/v1/documents/${id}`),

  // File upload - uploads a new version
  uploadFile: async (id: string, file: File, changeNotes?: string): Promise<ApiResponse<DocumentVersion>> => {
    const response = await api.upload<unknown>(
      `/v1/documents/${id}/upload`,
      file,
      changeNotes ? { changeNotes } : undefined
    );
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentVersion>(response.data) };
    }
    return response as ApiResponse<DocumentVersion>;
  },

  // Download current version
  downloadCurrentVersion: async (id: string): Promise<Blob | null> => {
    return api.download(`/v1/documents/${id}/download`);
  },

  // Get all versions
  getVersions: async (id: string): Promise<ApiResponse<DocumentVersion[]>> => {
    const response = await api.get<unknown>(`/v1/documents/${id}/versions`);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<DocumentVersion[]>(response.data) };
    }
    return response as ApiResponse<DocumentVersion[]>;
  },

  // Download specific version
  downloadVersion: async (id: string, versionNumber: number): Promise<Blob | null> => {
    return api.download(`/v1/documents/${id}/versions/${versionNumber}/download`);
  },

  // Get access logs
  getAccessLogs: async (id: string): Promise<ApiResponse<AccessLog[]>> => {
    const response = await api.get<unknown>(`/v1/documents/${id}/access-logs`);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<AccessLog[]>(response.data) };
    }
    return response as ApiResponse<AccessLog[]>;
  },
};

// PFile (Personnel File Jacket) types
export interface PFile {
  id: string;
  mdaId: string;
  staffId: string;
  fileNumber: string;
  fileNumberSequence: number;
  physicalLocation: string | null;
  building: string | null;
  room: string | null;
  cabinet: string | null;
  shelf: string | null;
  box: string | null;
  status: 'active' | 'inactive' | 'archived' | 'transferred' | 'destroyed';
  currentCustodianId: string | null;
  custodianSince: string | null;
  isCheckedOut: boolean;
  checkedOutTo: string | null;
  checkedOutAt: string | null;
  checkoutReason: string | null;
  expectedReturnDate: string | null;
  retentionPeriodYears: number | null;
  retentionStartDate: string | null;
  dispositionDate: string | null;
  dispositionAction: string | null;
  notes: string | null;
  tags: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  staff: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string | null;
    department?: { id: string; name: string } | null;
  };
  custodian?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  checkedOutToUser?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  documentCount: number;
}

export interface PFileMovement {
  id: string;
  pfileId: string;
  mdaId: string;
  movementType: string;
  fromUserId: string | null;
  toUserId: string | null;
  fromLocation: string | null;
  toLocation: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  requestReason: string | null;
  notes: string | null;
  performedBy: string;
  performedAt: string;
}

export interface PFileRequest {
  id: string;
  pfileId: string;
  mdaId: string;
  requestedBy: string;
  requestedAt: string;
  purpose: string;
  urgency: string;
  neededBy: string | null;
  status: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  dueDate: string | null;
  pfile: {
    id: string;
    fileNumber: string;
    staff: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
  requester: {
    id: string;
    firstName: string;
    lastName: string;
  };
  reviewer?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface PFileStats {
  total: number;
  active: number;
  checkedOut: number;
  archived: number;
  pendingRequests: number;
}

export interface CreatePFileInput {
  staffId: string;
  fileNumber?: string;
  physicalLocation?: string;
  building?: string;
  room?: string;
  cabinet?: string;
  shelf?: string;
  box?: string;
  retentionPeriodYears?: number;
  notes?: string;
  tags?: string[];
  documentIds?: string[];
}

export interface UnassignedDocument {
  id: string;
  title: string;
  description: string | null;
  referenceNumber: string | null;
  createdAt: string;
  docTypeName: string | null;
  docTypeCode: string | null;
  className: string | null;
  classColor: string | null;
  statusName: string | null;
  statusColor: string | null;
}

export interface UpdatePFileInput {
  physicalLocation?: string;
  building?: string;
  room?: string;
  cabinet?: string;
  shelf?: string;
  box?: string;
  status?: 'active' | 'inactive' | 'archived' | 'transferred' | 'destroyed';
  retentionPeriodYears?: number;
  notes?: string;
  tags?: string[];
  currentCustodianId?: string;
}

// PFiles API
export const institutionPFiles = {
  list: (params?: {
    page?: number;
    perPage?: number;
    staffId?: string;
    status?: string;
    isCheckedOut?: boolean;
    custodianId?: string;
    search?: string;
  }) => transformedGetWithMeta<PFile[]>('/v1/pfiles', params as Record<string, string | number | boolean | undefined>),

  getById: (id: string) => transformedGet<PFile>(`/v1/pfiles/${id}`),

  getByStaffId: (staffId: string) => transformedGet<PFile>(`/v1/pfiles/by-staff/${staffId}`),

  getStats: () => transformedGet<PFileStats>('/v1/pfiles/stats'),

  getPendingRequests: () => transformedGet<PFileRequest[]>('/v1/pfiles/requests'),

  create: async (input: CreatePFileInput): Promise<ApiResponse<PFile>> => {
    const response = await api.post<unknown>('/v1/pfiles', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  update: async (id: string, input: UpdatePFileInput): Promise<ApiResponse<PFile>> => {
    const response = await api.put<unknown>(`/v1/pfiles/${id}`, input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  checkout: async (id: string, reason: string, expectedReturnDate?: string): Promise<ApiResponse<PFile>> => {
    const response = await api.post<unknown>(`/v1/pfiles/${id}/checkout`, { reason, expectedReturnDate });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  checkin: async (id: string, notes?: string, newLocation?: string): Promise<ApiResponse<PFile>> => {
    const response = await api.post<unknown>(`/v1/pfiles/${id}/checkin`, { notes, newLocation });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  transfer: async (id: string, toUserId: string, notes?: string): Promise<ApiResponse<PFile>> => {
    const response = await api.post<unknown>(`/v1/pfiles/${id}/transfer`, { toUserId, notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  updateLocation: async (id: string, location: {
    physicalLocation?: string;
    building?: string;
    room?: string;
    cabinet?: string;
    shelf?: string;
    box?: string;
  }): Promise<ApiResponse<PFile>> => {
    const response = await api.put<unknown>(`/v1/pfiles/${id}/location`, location);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFile>(response.data) };
    }
    return response as ApiResponse<PFile>;
  },

  requestFile: async (id: string, purpose: string, urgency?: string, neededBy?: string): Promise<ApiResponse<PFileRequest>> => {
    const response = await api.post<unknown>(`/v1/pfiles/${id}/request`, { purpose, urgency: urgency || 'normal', neededBy });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFileRequest>(response.data) };
    }
    return response as ApiResponse<PFileRequest>;
  },

  reviewRequest: async (requestId: string, action: 'approve' | 'deny', notes?: string, dueDate?: string): Promise<ApiResponse<PFileRequest>> => {
    const response = await api.post<unknown>(`/v1/pfiles/requests/${requestId}/review`, { action, notes, dueDate });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<PFileRequest>(response.data) };
    }
    return response as ApiResponse<PFileRequest>;
  },

  getMovements: (id: string) => transformedGet<PFileMovement[]>(`/v1/pfiles/${id}/movements`),

  getDocuments: (id: string) => transformedGet<DocumentDetail[]>(`/v1/pfiles/${id}/documents`),

  getUnassignedDocuments: (staffId?: string) =>
    transformedGet<UnassignedDocument[]>('/v1/pfiles/unassigned-documents', staffId ? { staffId } : undefined),
};
